import { useState } from "react";
import { useAppDispatch } from "app/hooks";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

// Components
import ProText from "components/Pro/ProText";
import CustomIcon from "components/CustomIcon";
import { Box, Link, Typography, IconButton, Tooltip, useTheme } from "@mui/material";
import ProTooltip, { ProTooltipTitle } from "components/Pro/Tooltip";
import { SquaredAvatar, CircledButton } from "pages/ProjectDashboard/styled-components";

// Store
import { clearChart } from "store/slice/charts";

// Utils
import { isEmpty } from "lodash";
import { format } from "date-fns";

// Others
import icons from "enums/icons";
import { getNameInitials } from "utils";
import { urlPaths } from "enums/urlPaths";
import { getInsightsList, getSearchAIList } from "../../adapters/formattedData";

// Ability
import { useAbility } from "@casl/react";
import { AbilityContext } from "context/Ability";
import { InsightData, ProjectDataAvatarHistory } from "services/projects/project.types";
import { fetchProjectByIdService } from "services/projects/project.service";
import { RequestInfo } from "store/slice/store.types";
import { TruncatedText } from "components/TruncatedText";

const COLLABORATORS_LIMIT = 5;

type ConvertedInsightData = ReturnType<typeof getInsightsList>[0];
type ConvertedSearchAiData = ReturnType<typeof getSearchAIList>[0];

const ExpandableCardRowLabel = ({
    type,
    data,
    setShowInfoForInsight,
}: {
    type: "insight" | "searchAi";
    data: ConvertedInsightData | ConvertedSearchAiData;
    setShowInfoForInsight?: React.Dispatch<
        React.SetStateAction<{
            id: number;
            clickedOn: "Path" | "Brief" | "";
        }>
    >;
}) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const hasPeers = Boolean((data as ConvertedInsightData)?.meta?.peers);
    const hasSlt = Boolean((data as ConvertedInsightData)?.meta?.slt);
    const hasVcp = Boolean((data as ConvertedInsightData)?.meta?.vcp);

    const hasInsight = Boolean(hasPeers && hasSlt && hasVcp);

    const handleClick = () => {
        if (type === "insight") {
            const insightData = data as ConvertedInsightData;
            const { companyId, projectId } = insightData.meta;

            if (hasInsight) {
                dispatch(clearChart());
                navigate(
                    `${urlPaths.LeadershipOverview}/${companyId}?projectId=${projectId}&insightId=${insightData.id}`
                );
            } else {
                setShowInfoForInsight &&
                    setShowInfoForInsight({
                        id: data.id,
                        clickedOn: "Brief",
                    });
            }
        }

        if (type === "searchAi") {
            const searchAiData = data as ConvertedSearchAiData;
            const { companyId, projectId, relatedInsightId, relatedBriefId } = searchAiData.meta;

            navigate(
                `${urlPaths.Talent}/${companyId}?projectId=${projectId}&insightId=${relatedInsightId}&briefId=${relatedBriefId}&searchAiId=${searchAiData.id}`
            );
        }
    };

    return (
        <Box sx={{ display: "flex", maxWidth: 280, mr: 1 }}>
            <TruncatedText>
                <Link
                    component="button"
                    onClick={handleClick}
                    sx={{
                        mx: 1,
                        fontSize: 16,
                        fontWeight: 500,
                        letterSpacing: 0.5,
                        color: "inherit",
                        textDecoration: "none",
                        "&:hover": {
                            textDecoration: "underline",
                        },
                    }}
                >
                    {data.name}
                </Link>
            </TruncatedText>
        </Box>
    );
};

const ExpandableCardRow = ({
    data,
    type,
    isCompleted = false,
    isDisabled = false,
    setShowInfoForInsight,
    onClickRelatedButton,
    children,
}: {
    data: ConvertedInsightData | ConvertedSearchAiData;
    type: "insight" | "searchAi";
    isCompleted?: boolean;
    isDisabled?: boolean;
    setShowInfoForInsight?: React.Dispatch<
        React.SetStateAction<{
            id: number;
            clickedOn: "Path" | "Brief" | "";
        }>
    >;
    onClickRelatedButton: (id: number, type: "insight" | "searchAi" | "brief" | "company" | "people") => void;
    children: React.ReactNode;
}) => {
    const intl = useIntl();
    const theme = useTheme();

    const [isExpanded, setIsExpanded] = useState(false);
    const [avatarHistory, setAvatarHistory] = useState<{
        status: RequestInfo;
        data: ProjectDataAvatarHistory[];
    }>({
        status: "pristine",
        data: [],
    });
    const [openProTooltip, setOpenProTooltip] = useState(0);

    const ability = useAbility(AbilityContext);

    // Since design-wise we always show the link button for Briefs, this permissions is a workaround until we have a propper permission
    const hasProPermission = ability.can("see", "SAAS_LD_PROJECT_BRIEF_CREATE") || !ability.can("see", "LD_FREE");

    const handleSetExpanded = async () => {
        setIsExpanded((currentVal) => !currentVal);

        if (!isExpanded && !avatarHistory.data.length && avatarHistory.status === "pristine") {
            try {
                setAvatarHistory({ ...avatarHistory, status: "fetching" });

                const { data: insightData } = await fetchProjectByIdService<InsightData>(
                    Number(data.meta.projectId),
                    data.meta.ref,
                    { include_avatars: true }
                );

                setAvatarHistory({ data: insightData.avatar_history, status: "done" });
            } catch (error) {
                console.error(error);
            }
        }
    };

    return (
        <Box
            sx={{
                width: "100%",
                padding: isCompleted ? "12px" : "12px 8px",
                display: "flex",
                borderRadius: 0.5,
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: isDisabled ? "neutrals.light" : isCompleted ? "primary.light" : "common.white",
            }}
        >
            <Box
                sx={{
                    width: !isExpanded ? "50%" : "100%",
                    display: "flex",
                    alignItems: "center",
                    transition: "width 350ms",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flex: !isExpanded || isDisabled ? 0 : 1,
                        boxShadow:
                            !isExpanded || isDisabled
                                ? "none"
                                : "0px 1.67px 2.51px 0px #0000001A, 0px 0.83px 1.67px 0px #0000000F",
                    }}
                >
                    <SquaredAvatar
                        isDisabled={isDisabled}
                        hideBorder={isExpanded || isDisabled}
                        variant="square"
                        size={50}
                    >
                        <Typography>
                            {data.meta.createdOn ? getNameInitials(data.meta.createdOn.name).slice(0, 2) : ""}
                        </Typography>
                    </SquaredAvatar>

                    {!isCompleted && (
                        <IconButton size="small" sx={{ p: "4px" }} onClick={handleSetExpanded}>
                            <CustomIcon
                                type="outlined"
                                icon={!isExpanded ? icons.chevronRightThin : icons.chevronLeftThin}
                                sx={{ fontSize: 24, color: theme.palette.button.active.from }}
                            />
                        </IconButton>
                    )}

                    {/* Meta */}
                    {!isDisabled ? (
                        <Box
                            sx={{
                                ml: 1,
                                overflow: "hidden",
                                transition: "all 350ms 350ms",
                                display: !isExpanded ? "none" : "block",
                            }}
                        >
                            <Box display="flex">
                                {!isEmpty(data.meta.lastUpdatedOn) && (
                                    <Typography sx={{ color: "secondary.main", whiteSpace: "nowrap", mr: 1 }}>
                                        <b>Last modified: </b>
                                        {format(new Date(data.meta.lastUpdatedOn.datetime_utc), "d LLLL yyyy, HH:mm")}
                                    </Typography>
                                )}
                                <Typography sx={{ color: "secondary.main", whiteSpace: "nowrap", mr: 2 }}>
                                    <b>Created on: </b>
                                    {data.meta.createdOn
                                        ? format(new Date(data.meta.createdOn.datetime_utc), "d LLLL yyyy, HH:mm")
                                        : "N/A"}
                                </Typography>
                            </Box>
                            {!isEmpty(data.meta.lastUpdatedOn) && (
                                <Typography sx={{ color: "secondary.main", whiteSpace: "nowrap" }}>
                                    <b>Last modified by:</b> {data.meta.lastUpdatedOn.name}
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        <></>
                    )}

                    {/* Collabs */}
                    {!isDisabled && (
                        <Box
                            sx={{
                                display: !isExpanded ? "none" : "flex",
                                opacity: !isExpanded ? 0 : 1,
                                alignItems: "center",
                                ml: "auto",
                                pr: 1.6,
                                "& > div": {
                                    ml: -0.5,
                                },
                            }}
                        >
                            {data.meta.collaborators?.map((c) => (
                                <SquaredAvatar key={c.id} variant="square" size={28}>
                                    <Typography sx={{ fontWeight: "500 !important", fontSize: "0.5em !important" }}>
                                        {getNameInitials(c.name || "").slice(0, 2)}
                                    </Typography>
                                </SquaredAvatar>
                            ))}

                            {data.meta.collaborators?.length > COLLABORATORS_LIMIT && (
                                <SquaredAvatar variant="square" size={28} sx={{ backgroundColor: "background.grey" }}>
                                    <Typography sx={{ fontWeight: "500 !important", fontSize: "0.5em !important" }}>
                                        +{data.meta.collaborators.length - COLLABORATORS_LIMIT}
                                    </Typography>
                                </SquaredAvatar>
                            )}
                        </Box>
                    )}
                </Box>

                <ExpandableCardRowLabel
                    type={type}
                    data={data as ConvertedInsightData}
                    setShowInfoForInsight={setShowInfoForInsight}
                />

                {/* {type === "insight" ? (
                    <ExpandableCardRowLabel
                        data={data as ConvertedInsightData}
                        setShowInfoForInsight={setShowInfoForInsight}
                    />
                ) : (
                    <Typography sx={{ mx: 1, fontSize: 15, textAlign: "left" }}>{data.name}</Typography>
                )} */}

                {isCompleted ? (
                    <></>
                ) : (
                    <>
                        {!isExpanded && (
                            <Tooltip
                                arrow
                                placement="bottom-start"
                                title={intl.formatMessage({ id: "dashboard.insights.linkButton.tooltip" })}
                            >
                                <CircledButton
                                    isDisabled={!hasProPermission}
                                    size={28}
                                    component="button"
                                    onClick={() => hasProPermission && onClickRelatedButton(data.id, type)}
                                    color={data.shouldHighlight ? "hovered" : "hovered2"}
                                    sx={{
                                        filter: "drop-shadow(0px 1px 0px rgba(0, 0, 0, 0.05)) drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.05)) drop-shadow(0px 10px 10px rgba(0, 0, 0, 0.1))",
                                    }}
                                >
                                    <CustomIcon icon={icons.link} sx={{ fontSize: 16 }} />
                                </CircledButton>
                            </Tooltip>
                        )}

                        {!hasProPermission && (
                            <ProTooltip
                                open={openProTooltip === 1}
                                setOpen={setOpenProTooltip}
                                title={<ProTooltipTitle onClose={() => setOpenProTooltip(0)} />}
                                innerClickAway={true}
                            >
                                <Box sx={{ ml: 1 }}>
                                    <ProText onClick={() => setOpenProTooltip(1)} />
                                </Box>
                            </ProTooltip>
                        )}
                    </>
                )}
            </Box>

            <Box
                sx={{
                    width: !isExpanded ? "50%" : "0%",
                    display: !isExpanded ? "flex" : "none",
                    alignItems: "center",
                    justifyContent: "flex-end",
                }}
            >
                {!isDisabled && children}
            </Box>
        </Box>
    );
};

export default ExpandableCardRow;
